import { createFileRoute, Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { Layout } from "@/components/Layout";
import { ProductCard } from "@/components/ProductCard";
import {
  PRODUCTS_QUERY,
  storefrontApiRequest,
  type ShopifyProduct,
} from "@/lib/shopify";
import { useMarket, useT, marketFromSearch, type Market } from "@/i18n";

const featuredQueryOptions = (market: Market) =>
  queryOptions({
    queryKey: ["products", "featured-grid", market] as const,
    queryFn: async () => {
      try {
        const res = await storefrontApiRequest(PRODUCTS_QUERY, {
          first: 12,
          query: null,
          country: market === "us" ? "US" : "FI",
          language: "EN", // primary locale holds curated Finnish copy
        });
        return (res?.data?.products?.edges ?? []) as ShopifyProduct[];
      } catch (err) {
        console.error("[/products] fetch failed:", err);
        return [] as ShopifyProduct[];
      }
    },
  });

export const Route = createFileRoute("/products")({
  loader: ({ context, location }) => {
    // Prime both market variants when SSR'd so the initial paint is correct
    // whether the URL is /products or /products?market=us.
    const market = marketFromSearch(location.searchStr);
    return context.queryClient.ensureQueryData(featuredQueryOptions(market));
  },
  head: () => ({
    meta: [
      {
        title:
          "Suositut tuotteet — Pikler-telineet ja oppimistornit | Montessori24",
      },
      {
        name: "description",
        content:
          "Pikler-kiipeilytelineet, oppimistornit ja Montessori-kalusteet valituilta eurooppalaisilta valmistajilta. Suomalainen erikoisverkkokauppa.",
      },
      {
        property: "og:title",
        content:
          "Suositut tuotteet — Pikler-telineet ja oppimistornit",
      },
      {
        property: "og:description",
        content:
          "Pikler-kiipeilytelineet, oppimistornit ja Montessori-kalusteet eurooppalaisilta valmistajilta. Suomalainen erikoisverkkokauppa.",
      },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Montessori24" },
      { property: "og:locale", content: "fi_FI" },
      { property: "og:url", content: "https://montessori24.fi/products" },
      { property: "og:image", content: "https://montessori24.fi/og-image.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      {
        name: "twitter:title",
        content:
          "Suositut tuotteet — Pikler-telineet ja oppimistornit",
      },
      { name: "twitter:image", content: "https://montessori24.fi/og-image.jpg" },
      { name: "robots", content: "noindex, follow" },
    ],
    links: [
      // English-language legacy listing. It duplicates the Finnish shop, so
      // it stays out of the index and consolidates all signals onto /kauppa
      // instead of self-canonicalising a noindex duplicate.
      { rel: "canonical", href: "https://montessori24.fi/kauppa" },
      { rel: "alternate", hrefLang: "fi", href: "https://montessori24.fi/kauppa" },
      { rel: "alternate", hrefLang: "x-default", href: "https://montessori24.fi/kauppa" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Suositut tuotteet — Montessori24",
          url: "https://montessori24.fi/products",
          isPartOf: {
            "@type": "WebSite",
            name: "Montessori24",
            url: "https://montessori24.fi",
          },
          breadcrumb: {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Etusivu", item: "https://montessori24.fi/" },
              { "@type": "ListItem", position: 2, name: "Tuotteet", item: "https://montessori24.fi/products" },
            ],
          },
        }),
      },
    ],
  }),
  component: ProductsIndex,
});

function ProductsIndex() {
  const market = useMarket();
  const t = useT();
  const navigate = useNavigate();
  const search = useRouterState({
    select: (s) => s.location.search as Record<string, unknown>,
  });

  const options = useMemo(() => featuredQueryOptions(market), [market]);
  const { data: products } = useSuspenseQuery(options);

  // Keep the URL's ?market= in sync with the resolved market so shared links
  // survive a copy/paste round-trip. Only writes when it differs.
  useEffect(() => {
    if (market === "us" && search.market !== "us") {
      navigate({ to: "/products", search: { ...search, market: "us" }, replace: true });
    }
  }, [market, navigate, search]);

  const isUs = market === "us";
  const viewAllHref = "/kauppa" as const;

  return (
    <Layout>
      <section className="border-b border-border bg-cream">
        <div className="container-prose px-4 pt-16 pb-6 md:px-6 md:pt-24 md:pb-10">
          <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-muted-foreground">
            {t("products.eyebrow")}
          </p>
          <h1 className="mt-3 font-display text-4xl italic tracking-[-0.01em] text-foreground md:text-6xl text-balance">
            {t("products.title")}
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground md:text-base">
            {t("products.subtitle")}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3 text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
            <span>{isUs ? "🇺🇸 US · USD" : "🇫🇮 FI · EUR"}</span>
            <span aria-hidden className="h-px w-6 bg-border" />
            <span>
              {isUs
                ? "Free US shipping · 6–10 business days"
                : "Ilmainen toimitus · 3–7 arkipäivää"}
            </span>
          </div>
        </div>
      </section>

      <section className="bg-cream">
        <div className="container-prose px-4 py-14 md:px-6 md:py-20">
          {products.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-sand/40 p-12 text-center text-sm text-muted-foreground">
              {t("products.empty")}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-x-4 gap-y-10 md:grid-cols-3 md:gap-x-6 md:gap-y-14">
                {products.map((p, i) => (
                  <ProductCard
                    key={p.node.id}
                    product={p}
                    railName="products_index"
                    railPosition={i}
                    sizes="(min-width: 1024px) 32vw, (min-width: 640px) 45vw, 45vw"
                  />
                ))}
              </div>
              <div className="mt-12 flex justify-center">
                <Link
                  to={viewAllHref}
                  search={isUs ? { market: "us" } : undefined}
                  className="inline-flex items-center gap-2 rounded-full bg-foreground px-6 py-3 text-sm font-medium text-background transition-transform hover:-translate-y-0.5"
                >
                  {t("products.viewAll")}
                </Link>
              </div>
            </>
          )}
        </div>
      </section>
    </Layout>
  );
}